// The home page's broadcast: a live Secret Sample, or the next one coming
// up. Nothing at all when there's neither. Takes you to /events.
//
// Live: the record spins and the strip glows. Upcoming: the record sits
// still and the strip says when. More than one: the most pressing one, and
// "+N more".

import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import type { ApiEventList } from "@shared/api-types.js";
import { webApi } from "@web-shared/lib/web-api";
import { cn } from "@web-shared/lib/utils";
import { MiniRecord, soonOf } from "./EventParts";

type ListedEvent = ApiEventList["events"][number];

/** Live ones first, then what starts soonest. */
function featuredOf(events: ListedEvent[]): { lead: ListedEvent; more: number } | null {
  const live = events.filter((e) => e.status === "assigned");
  const upcoming = events
    .filter((e) => e.status === "open")
    .sort((a, b) => (a.startsAt ?? "9999").localeCompare(b.startsAt ?? "9999"));
  const all = [...live, ...upcoming];
  const lead = all[0];
  return lead ? { lead, more: all.length - 1 } : null;
}

export function EventsBanner({ className }: { className?: string }) {
  const list = useQuery({
    // Not under ["web"]: who's signed in doesn't change what's on.
    queryKey: ["events-banner"],
    queryFn: () => webApi<ApiEventList>("/events"),
    staleTime: 60_000,
    // An upcoming event goes live while the page is open.
    refetchInterval: 60_000,
  });
  const featured = list.data ? featuredOf(list.data.events) : null;
  if (!featured) return null;
  const { lead, more } = featured;
  const live = lead.status === "assigned";
  const when = live ? null : soonOf(lead.startsAt);

  return (
    <Link
      to="/events"
      className={cn(
        "group relative flex items-center gap-4 overflow-hidden rounded-2xl border p-3 pr-4 transition-colors sm:gap-6 sm:p-4 sm:pr-6",
        live
          ? "border-primary/40 shadow-[0_0_70px_-25px_var(--primary)] hover:border-primary/60"
          : "border-white/10 hover:border-primary/30",
        className,
      )}
      style={{
        background: live
          ? "linear-gradient(100deg, color-mix(in srgb, var(--primary) 16%, #101013) 0%, #101013 55%, rgba(28,57,142,0.35) 100%)"
          : "linear-gradient(100deg, color-mix(in srgb, var(--primary) 8%, #101013) 0%, #101013 60%, rgba(28,57,142,0.22) 100%)",
      }}
    >
      {/* Faint grooves across the strip, the record's echo. */}
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 top-1/2 size-80 -translate-y-1/2 rounded-full opacity-[0.07]"
        style={{ background: "repeating-radial-gradient(circle at center, #fff 0 1px, transparent 1px 6px)" }}
      />

      <MiniRecord spinning={live} className="size-14 sm:size-20" />

      <div className="relative min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          {live ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-primary-foreground">
              <span className="size-1.5 animate-pulse rounded-full bg-primary-foreground" /> Live now
            </span>
          ) : (
            <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-foreground">
              {when ?? "Coming up"}
            </span>
          )}
          <span className="hidden text-[10px] font-bold uppercase tracking-[0.25em] text-primary sm:inline">Secret Sample</span>
          {more > 0 && <span className="text-[11px] text-muted-foreground">+{more} more</span>}
        </div>
        <p className="mt-1 truncate text-lg font-black tracking-tight sm:text-2xl">{lead.title}</p>
        <p className="truncate text-xs text-muted-foreground sm:text-sm">
          {live ? (
            <>
              <span className="sm:hidden">Seeds are out. Flip one.</span>
              <span className="hidden sm:inline">Seeds are out. Jump on the call and flip one.</span>
            </>
          ) : (
            <>
              <span className="sm:hidden">Get someone's seed. Flip it live.</span>
              <span className="hidden sm:inline">Drop a loop in the pool, get someone else's seed, flip it live.</span>
            </>
          )}
        </p>
      </div>

      <span
        className={cn(
          "relative hidden shrink-0 items-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-bold transition-transform group-hover:translate-x-0.5 sm:inline-flex",
          live ? "bg-primary text-primary-foreground" : "bg-white/10 text-foreground",
        )}
      >
        {live ? "Join in" : "Count me in"} <ArrowRight className="size-4" />
      </span>
      <ArrowRight className="relative size-5 shrink-0 text-muted-foreground sm:hidden" />
    </Link>
  );
}
